import { is_device_action_ui } from '../../../../types/DeviceActionUI';
import { is_group_action_ui } from '../../../../types/GroupActionUI';
import { is_notification_action_ui } from '../../../../types/NotificationActionUI';
import {
	is_enable_disable_scene_action_ui,
	is_play_scene_action_ui,
} from '../../../../types/SceneActionUI';
import {
	_device_action_msg,
	_group_action_msg,
	_notification_action_msg,
	findIconAndTitle,
	handle_action_timer_delay,
} from '../helpers/actions_utils';
import { createDeviceID, load_image_from, isWallDisplay } from '../../../../helpers/interface_helpers';
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { findImage, findSceneImage } from '../helpers/main_utils';
import { hasRadioFavorites } from '../../helpers/radio_favorites_cache';

import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { scenes } from 'src/stores/lists/scenes';

//Types
import type { GroupActionUI } from 'src/types/GroupActionUI';
import type { DeviceActionUI } from 'src/types/DeviceActionUI';
import type { NotificationActionUI } from 'src/types/NotificationActionUI';
import type { SceneActionUI } from 'src/types/SceneActionUI';

let _format;
let _f = format.subscribe((e) => (_format = e));

const data_from_notification_action = (translated: NotificationActionUI) => {
	let labelList = [],
		actionList = [],
		withLabelList = false,
		title = 'Action',
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Action Description', bold: false },
		description = { value: '', icon: 'fa fa-circle' };

	let iconAndTitle = findIconAndTitle(translated.id);
	title =
		iconAndTitle.title !== undefined ? _format(iconAndTitle.title).toLocaleUpperCase() : 'UNKNOWN';

	let msg = _notification_action_msg(
		translated.msg,
		translated.msg_type,
		translated.delay,
		translated.timer,
	);

	subtitle.value = msg.subtitle;
	actionList = [...msg.actionList];

	avatar = { icon: iconAndTitle.icon };

	return {
		title,
		subtitle,
		labelList,
		actionList,
		withLabelList,
		avatar,
		description,
	};
};

const data_from_device_action = (translated: DeviceActionUI) => {
	let labelList = [],
		conditionList = [],
		actionList = [],
		withLabelList = false,
		title = 'Action',
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Action Description', bold: false },
		description = { value: '', icon: 'fa fa-circle' };

	const _devices = get(devices);

	withLabelList = true;

	let device_id = createDeviceID(
		translated?.device?.id,
		translated?.device?.channel,
		_devices[translated?.device?.id]?.type,
	);
	const _device = _devices[device_id];

	title = _device?.name || _format('missing_device_label', { default: 'Missing Device' });

	const msg = _device_action_msg(
		_device?.category,
		translated,
		_device?.gen,
		device_id,
		_device?.mode,
	);
	if (msg) {
		labelList = [...msg.labelList];
		conditionList = [...msg.conditionList];
		actionList = [...msg.actionList];
		subtitle.value = msg.subtitle;
	}

	if (title === _format('missing_device_label', { default: 'Missing Device' })) {
		labelList = [
			{
				value: device_id,
				icon: 'fa-solid fa-microchip',
			},
			...labelList,
		];
	}

	const isWallDisplayMediaPlayer =
		_device?.category === 'media_player' && isWallDisplay(_device?.type);
	const hasRadioPlayFavouriteAction = translated.actions?.some(
		(action) => action.name === 'Radio_PlayFavourite',
	);

	if (isWallDisplayMediaPlayer && hasRadioPlayFavouriteAction && _device) {
		const radioFavoritesExist = hasRadioFavorites(device_id);
		if (radioFavoritesExist === false) {
			labelList = [
				{
					value: _format('no_favorite_radio_stations_title', {
						default: 'No favorite radio stations',
					}),
					icon: 'fas fa-warning',
					iconColor: '--color-warning',
				},
				...labelList,
			];
		}
	}

	avatar = findImage(_device);
	if (avatar.image === undefined && avatar.backgroundColor === undefined) {
		avatar = { icon: _device?.icon || 'text-warning fas fa-warning' };
	}

	return {
		title,
		subtitle,
		labelList,
		conditionList,
		actionList,
		withLabelList,
		avatar,
		description,
	};
};

const data_from_play_scene_action = (translated: SceneActionUI) => {
	let labelList = [],
		withLabelList = false,
		title = 'Action',
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Action Description', bold: false },
		description = { value: '', icon: 'fa fa-circle' };

	const _scenes = get(scenes);

	let scene = _scenes[translated?.scene_id];
	title = scene?._meta?.name || _format('unknown_scene', { default: 'Unknown scene' });

	let msg = _format('scene_exe_msg', { default: 'Play scene' });

	subtitle.value = msg;

	avatar = findSceneImage(scene);

	if (avatar.image === undefined && avatar.backgroundColor === undefined) {
		avatar = { icon: 'fa fa-trash' };
	}

	const { delay } = translated;
	const actionList = handle_action_timer_delay(undefined, delay);

	return {
		title,
		subtitle,
		labelList,
		actionList,
		withLabelList,
		avatar,
		description,
	};
};

const data_from_enable_disable_scene_action = (translated: SceneActionUI) => {
	let labelList = [],
		withLabelList = false,
		title = 'Action',
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Action Description', bold: false },
		description = { value: '', icon: 'fa fa-circle' };

	const _scenes = get(scenes);

	let scene = _scenes[translated?.scene_id];
	title = scene?._meta?.name || _format('unknown_scene', { default: 'Unknown scene' });

	let msg = _format(translated.option, { default: 'Play scene' });

	subtitle.value = msg;

	avatar = findSceneImage(scene);

	if (avatar.image === undefined && avatar.backgroundColor === undefined) {
		avatar = { icon: 'fa fa-trash' };
	}

	const { delay } = translated;
	const actionList = handle_action_timer_delay(undefined, delay);

	return {
		title,
		subtitle,
		labelList,
		actionList,
		withLabelList,
		avatar,
		description,
	};
};

const data_from_group_action = (translated: GroupActionUI) => {
	let labelList = [],
		withLabelList = false,
		actionList = [],
		title = 'Action',
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Action Description', bold: false },
		description = { value: '', icon: 'fa fa-circle' };

	withLabelList = true;

	const _groups = get(groups);
	let group = _groups[translated.group_id];

	title = group?.name || 'deleted group';
	avatar = {
		image: group?.image ? load_image_from(group?.image) : undefined,
		backgroundColor: group?.backgroundColor || undefined,
		icon: undefined,
	};

	if (avatar.image === undefined && avatar.backgroundColor === undefined) {
		avatar = { icon: 'fa fa-trash' };
	}

	let parsedAction = _group_action_msg(
		group?.type || undefined,
		translated.actions,
		translated.timer,
		translated.delay,
	);

	labelList = [...parsedAction.labelList];
	actionList = [...parsedAction.actionList];
	subtitle.value = parsedAction.subtitle;

	return {
		title,
		subtitle,
		labelList,
		actionList,
		withLabelList,
		avatar,
		description,
	};
};

export const extract_actions_variables = (
	translated: NotificationActionUI | DeviceActionUI | SceneActionUI | GroupActionUI | undefined,
) => {
	let labelList = [],
		actionList = [],
		withLabelList = false,
		title = 'Action',
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Action Description', bold: false },
		description = { value: '', icon: 'fa fa-circle' };

	if (is_notification_action_ui(translated)) {
		return data_from_notification_action(translated);
	} else if (is_device_action_ui(translated)) {
		return data_from_device_action(translated);
	} else if (is_play_scene_action_ui(translated)) {
		return data_from_play_scene_action(translated);
	} else if (is_enable_disable_scene_action_ui(translated)) {
		return data_from_enable_disable_scene_action(translated);
	} else if (is_group_action_ui(translated)) {
		return data_from_group_action(translated);
	} else {
		title = _format('scenes_title_unknown_action', {
			default: 'Unknown action',
		});
		subtitle.value = '';
		labelList = [];
		actionList = [];
		withLabelList = false;
	}

	return {
		title,
		subtitle,
		labelList,
		withLabelList,
		actionList,
		avatar,
		description,
	};
};
